---
id: image-service-go
title: Image service
owner: Backend Team
status: Active
lastReviewed: 2026-02-02
visibility: internal
tags:
  - image
  - conversion
  - resume
  - microservice
---

## Product Overview

### Who it serves

This microservice supports resume and document workflows that require image conversions and profile picture resizing for downstream product experiences.

### Primary workflow

Clients submit a GCS URL to convert PDFs or DOCX files into JPEG images, or trigger profile picture checks that resize large resume profile images and write the resized version back to storage.

### Business rules

Conversion accepts only PDF or DOCX files and returns base64-encoded images per page. Resume profile picture resizing only runs for objects stored under the resume profile picture directory and larger than the size threshold.

### Outcomes

Converted images are returned to callers and stored in GCS, while oversized profile pictures are resized and re-uploaded to the original object path for consistent downstream rendering.

### Out of scope

Authentication, UI rendering, and non-resume image management are handled by other services.

### API documentation

[Internal API Documentation](https://wiki.rezi.ai/apis/image-service-go-api-docs)

## Technical Overview

### Architecture flow

Routes in `apps/micro-services/image-service-go/pkg/api/routes/routes.go` map conversion and resize requests to controller handlers that invoke conversion and GCS processing services.

### Entry points

- The API server runs from `apps/micro-services/image-service-go/cmd/image/main.go`
- OpenAPI generation is provided by `apps/micro-services/image-service-go/cmd/spec/main.go`

### Key modules

Request handling lives in `apps/micro-services/image-service-go/pkg/api/controllers`, conversion logic in `apps/micro-services/image-service-go/pkg/services/conversionService.go`, and resize orchestration plus GCS helpers in `apps/micro-services/image-service-go/pkg/services/resizeImgService.go` and `apps/micro-services/image-service-go/pkg/services/gcsService.go`.

### Data sources and sinks

The service reads and writes files in GCS buckets, using GCS URLs as inputs and storing resized or converted image artifacts back to the configured resume image bucket.

### Integrations and side effects

Conversion relies on ImageMagick and LibreOffice binaries for PDF and DOCX processing, calls the image-resize service for oversized profile pictures, and uses GCS client APIs for object listing and uploads.

### Constraints and risks

Processing depends on external binaries and network access to GCS and the image-resize service; missing binaries, credentials, or bucket access will cause conversion or resizing to fail.

## Changelog

- 2026-02-02: Initial documentation.
